import {ProcedureType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {
  QueryResultType,
  SkippableBenchlingQueryResult,
  useSkippableBenchlingQuery,
} from 'graphql-client/BenchlingQuery';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import isProcedureVersionPoW from 'procedures/design/utils/isProcedureVersionPoW';
import buildProcessFlowchartForValidationFromGQL from 'procedures/design/validation/buildProcessFlowchartForValidationFromGQL';
import {ProcedureValidationError} from 'procedures/design/validation/ProcedureValidationTypes';
import {validateProcedure} from 'procedures/design/validation/validateProcedure';
import {
  ProcedurePageFlowchartVersionQuery,
  ProcedurePageFlowchartVersionQueryVariables,
} from 'procedures/graphql/__generated/ProcedurePageFlowchartVersionQuery';
import {
  ProcedurePageThinQuery,
  ProcedurePageThinQueryVariables,
} from 'procedures/graphql/__generated/ProcedurePageThinQuery';
import {
  PROCEDURE_PAGE_FLOWCHART_VERSION_QUERY,
  PROCEDURE_PAGE_THIN_QUERY,
} from 'procedures/graphql/ProceduresQueries';
import parseProcedureFromNetwork from 'procedures/network/parseProcedureFromNetwork';
import {MethodNode, ProcedureWithMetadata} from 'procedures/ProcedureDomainTypes';
import {NetworkProcedure} from 'procedures/ProcedurePageComponents';
import {generateIdToCidMap} from 'procedures/procedurePagePersistenceUtils';
import {extractMethodNodeFromProcessNode} from 'procedures/processes/MethodNodeUtils';
import {getPowAgnosticPublishedFlowchartVersionId} from 'procedures/utils/powAgnosticFlowchartHelpers';
import {
  useAllTagSchemasWithProceduresShape,
  useAllTagSchemasWithStandardShape,
} from 'procedures/utils/useAllTagSchemas';
import useArchivedLibraryParameterDefinitionIds, {
  getLibraryParameterDefinitionIdsFromMethods,
} from 'procedures/utils/useArchivedLibraryParameterDefinitionIds';
import useBioentitiesForProcedures, {
  getBioentityIdsFromMethods,
} from 'procedures/utils/useBioentitiesForProcedures';
import useSchemaInterfacesWithFields from 'procedures/utils/useSchemaInterfacesWithFields';
import {FlowchartEditorMode} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/ProcessFlowchartEditorModeConstants';
import {PLUGIN_REGISTRY_INSTANCE} from 'processes/graph-builder-processes/platform/PluginRegistry';
import {
  ProcessFlowchartForValidation,
  ProcessFlowchartValidationError,
  ValidationContext,
} from 'processes/graph-builder-processes/validation/ProcessFlowchartForValidationTypes';
import {ProcessFlowchartDesignSpace} from 'processes/graph-builder-processes/validation/ProcessGraphBuilderConstants';
import {useProcessFlowchartConfigQuery} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/useProcessFlowchartConfigQuery';

export enum ProcedureValidationErrorType {
  NOT_PUBLISHED = 'NOT_PUBLISHED',
  NOT_ACCESSIBLE = 'NOT_ACCESSIBLE',
  NOT_LOADED = 'NOT_LOADED',
  INTERNAL_VALIDATION_ERRORS = 'INTERNAL_VALIDATION_ERRORS',
}
// An "external" validation error precedes inspection of the actual flowchart contents
type ExternalValidationErrorType = Exclude<ProcedureValidationErrorType, 'INTERNAL_VALIDATION_ERRORS'>;

export type ValidateProcedureResult =
  // If no procedure ID is provided, we return null
  | null
  | (
      | {
          // A valid procedure
          isValid: true;
        }
      | {
          // A procedure with a single EXTERNAL error (e.g. not loaded/published/accessible, etc.)
          isValid: false;
          errorType: ExternalValidationErrorType;
          errors?: never;
        }
      | {
          // A legacy procedure with INTERNAL validation errors
          isValid: false;
          errorType: ProcedureValidationErrorType.INTERNAL_VALIDATION_ERRORS;
          errors: Array<ProcedureValidationError>;
        }
      | {
          // A PoW procedure with INTERNAL validation errors (structural + procedure-level)
          isValid: false;
          errorType: ProcedureValidationErrorType.INTERNAL_VALIDATION_ERRORS;
          errors: Array<ProcessFlowchartValidationError>;
        }
    );

/**
 * Given a procedure ID, we load the procedure and validate it.
 * Supports both legacy procedures and PoW procedures.
 *
 * Returns:
 *  - null if the procedure ID is null
 *  - {isValid}, with either a `generalError` string or a full list of validation errors.
 *
 *  See the `ValidateProcedureResult` type above for details.
 */
export default function useValidateProcedureById(procedureId?: string | null): ValidateProcedureResult {
  // We need to load some global schemas for validation
  const tagSchemasForProcedures = useAllTagSchemasWithProceduresShape();
  const tagSchemas = useAllTagSchemasWithStandardShape();

  // Load the procedure, and check for external errors
  const procedureOrError = useFullProcedureLoader(procedureId ?? null);

  // Extract method nodes for bioentity loading (works for both legacy and PoW)
  const methodNodes: Array<MethodNode> = (() => {
    if (procedureOrError.procedureWithMetadata != null) {
      return procedureOrError.procedureWithMetadata.flowchart.nodes;
    }
    if (procedureOrError.processFlowchartForValidation != null) {
      return procedureOrError.processFlowchartForValidation.nodes
        .map((node) => extractMethodNodeFromProcessNode(node))
        .filter((node): node is MethodNode => node != null);
    }
    return [];
  })();

  // Final prep for internal validation: load the referenced bioentities and library parameter definitions
  const bioentityIdsInFlowchart = getBioentityIdsFromMethods(methodNodes);
  const bioentitiesForProcedures = useBioentitiesForProcedures(bioentityIdsInFlowchart);
  const libraryParameterDefinitionIds = getLibraryParameterDefinitionIdsFromMethods(methodNodes);
  const archivedLibraryParameterDefinitionIds = useArchivedLibraryParameterDefinitionIds(
    libraryParameterDefinitionIds
  );
  const schemaInterfaces = useSchemaInterfacesWithFields();

  if (procedureId == null) {
    return null;
  }

  // Report external errors that were found when loading the procedure
  if (procedureOrError.error != null) {
    return {
      isValid: false,
      errorType: procedureOrError.error,
    };
  }

  // PoW validation path: use the Procedures plugin's flowchart validator directly.
  if (procedureOrError.processFlowchartForValidation != null) {
    const plugin = PLUGIN_REGISTRY_INSTANCE.getPlugin('Procedures Plugin');
    const validator = plugin.flowchartValidator;
    if (validator == null) {
      return {isValid: true};
    }

    const validationContext: ValidationContext = {
      editorMode: FlowchartEditorMode.MAIN_CANVAS,
      designSpace: procedureOrError.designSpace,
      didInitialFlowchartHaveChainedRouters: false,
      transitions: {},
      disconnectedNodeIds: [],
      plugin,
      applicationValidationContext: {
        tagSchemasForProcedures,
        tagSchemas,
        bioentitiesForProcedures,
        archivedLibraryParameterDefinitionIds,
        schemaInterfaces,
        procedureType: procedureOrError.procedureType,
      },
    };

    const errors = validator.validateFlowchartForType(
      procedureOrError.processFlowchartForValidation,
      validationContext
    );

    return errors.length === 0
      ? {isValid: true}
      : {
          isValid: false,
          errorType: ProcedureValidationErrorType.INTERNAL_VALIDATION_ERRORS,
          errors,
        };
  }

  // Legacy validation path: validate via the parsed procedure flowchart
  const validationErrors = validateProcedure(procedureOrError.procedureWithMetadata.flowchart, {
    tagSchemasForProcedures,
    tagSchemas,
    bioentitiesForProcedures,
    archivedLibraryParameterDefinitionIds,
    schemaInterfaces,
    procedureType: procedureOrError.procedureWithMetadata.metadata.procedureType,
  });

  return validationErrors.length === 0
    ? {isValid: true}
    : {
        isValid: false,
        errorType: ProcedureValidationErrorType.INTERNAL_VALIDATION_ERRORS,
        errors: validationErrors,
      };
}

/**
 * Inspects the result of a ProcedurePageThinQuery to retrieve the ID of its published flowchart version.
 * It may also return an error state.
 *
 * Returns the version ID for both legacy and PoW procedures.
 *
 * NOTE: If procedure publishing is OFF, we inspect the LATEST flowchart version instead.
 */
function getPublishedFlowchartVersionId(
  procedureQueryResult: SkippableBenchlingQueryResult<ProcedurePageThinQuery, ProcedurePageThinQueryVariables>
): {error: ExternalValidationErrorType} | string {
  // Loading state
  if (procedureQueryResult.type !== QueryResultType.SUCCESS) {
    return {error: ProcedureValidationErrorType.NOT_LOADED};
  }

  // Auth issues
  if (isInaccessible(procedureQueryResult.data.procedure)) {
    return {error: ProcedureValidationErrorType.NOT_ACCESSIBLE};
  }

  const procedure = procedureQueryResult.data.procedure;
  const versionIdToCheck = getPowAgnosticPublishedFlowchartVersionId(procedure);

  if (versionIdToCheck == null) {
    // The "latest" flowchart is never null; this check is only meaningful when publishing is enabled
    return {error: ProcedureValidationErrorType.NOT_PUBLISHED};
  }

  return versionIdToCheck;
}

/**
 * Extracts the flowchart config ID from a successful thin procedure query.
 * Returns null if the query is not successful, the procedure is inaccessible,
 * or no processFlowchartConfig is present.
 */
function getPowFlowchartConfigId(
  procedureQueryResult: SkippableBenchlingQueryResult<ProcedurePageThinQuery, ProcedurePageThinQueryVariables>
): string | null {
  if (
    procedureQueryResult.type !== QueryResultType.SUCCESS ||
    isInaccessible(procedureQueryResult.data.procedure)
  ) {
    return null;
  }
  return procedureQueryResult.data.procedure.processFlowchartConfig?.id ?? null;
}

/**
 * @package Exported for testing/mocking
 */
export function useThinProcedureQuery(
  procedureId: string | null
): SkippableBenchlingQueryResult<ProcedurePageThinQuery, ProcedurePageThinQueryVariables> {
  return useSkippableBenchlingQuery<ProcedurePageThinQuery, ProcedurePageThinQueryVariables>(
    PROCEDURE_PAGE_THIN_QUERY,
    {
      variables: {procedureId: procedureId || ''},
      skip: procedureId == null,
      fetchPolicy: 'network-only',
    }
  );
}

function useLegacyFlowchartVersionQuery(
  procedureId: string | null,
  flowchartVersionId: string | null
): SkippableBenchlingQueryResult<
  ProcedurePageFlowchartVersionQuery,
  ProcedurePageFlowchartVersionQueryVariables
> {
  return useSkippableBenchlingQuery<
    ProcedurePageFlowchartVersionQuery,
    ProcedurePageFlowchartVersionQueryVariables
  >(PROCEDURE_PAGE_FLOWCHART_VERSION_QUERY, {
    variables: {procedureFlowchartVersionId: flowchartVersionId ?? ''},
    skip: procedureId == null || flowchartVersionId == null,
    fetchPolicy: 'network-only',
  });
}

type UseFullProcedureLoaderResult =
  // Legacy procedure loaded successfully
  | {
      procedureWithMetadata: ProcedureWithMetadata;
      processFlowchartForValidation?: never;
      designSpace?: never;
      procedureType?: never;
      error?: never;
    }
  // PoW procedure loaded successfully
  | {
      procedureWithMetadata?: never;
      processFlowchartForValidation: ProcessFlowchartForValidation;
      designSpace: ProcessFlowchartDesignSpace;
      procedureType: ProcedureType;
      error?: never;
    }
  // Error loading the procedure
  | {
      procedureWithMetadata?: never;
      processFlowchartForValidation?: never;
      designSpace?: never;
      procedureType?: never;
      error: ExternalValidationErrorType;
    };

/**
 * Hook to load a full procedure object for validation. Supports both legacy and PoW procedures.
 *
 * For legacy procedures: loads the thin procedure + flowchart version, then parses into ProcedureWithMetadata.
 * For PoW procedures: loads the thin procedure + flowchart config, then transforms into ProcessFlowchartForValidation.
 *
 * Reports "external" errors if any are found; otherwise, returns the loaded data.
 */
function useFullProcedureLoader(procedureId: string | null): UseFullProcedureLoaderResult {
  // Load the "thin" procedure (i.e., everything except the flowchart version, which holds the nodes & edges)
  const thinProcedureQueryResult = useThinProcedureQuery(procedureId);

  // Determine the published version ID and whether this is a PoW procedure
  const publishedVersionIdOrError = getPublishedFlowchartVersionId(thinProcedureQueryResult);
  const publishedVersionId: string | null =
    typeof publishedVersionIdOrError === 'string' ? publishedVersionIdOrError : null;
  const isPoW = publishedVersionId != null && isProcedureVersionPoW(publishedVersionId);

  // Legacy path: load the flowchart version (skipped for PoW)
  const legacyFlowchartVersionId = isPoW ? null : publishedVersionId;
  const legacyFlowchartVersionQueryResult = useLegacyFlowchartVersionQuery(
    procedureId,
    legacyFlowchartVersionId
  );

  // PoW path: load the flowchart config (skipped for legacy)
  const powFlowchartConfigId = isPoW ? getPowFlowchartConfigId(thinProcedureQueryResult) : null;
  const powFlowchartConfigQueryResult = useProcessFlowchartConfigQuery(
    powFlowchartConfigId,
    isPoW ? publishedVersionId : null,
    {networkOnly: true}
  );

  // Common error checks
  if (procedureId == null || thinProcedureQueryResult.type !== QueryResultType.SUCCESS) {
    return {error: ProcedureValidationErrorType.NOT_LOADED};
  }
  if (isInaccessible(thinProcedureQueryResult.data.procedure)) {
    return {error: ProcedureValidationErrorType.NOT_ACCESSIBLE};
  }
  if (typeof publishedVersionIdOrError === 'object') {
    return {error: publishedVersionIdOrError.error};
  }

  // PoW path: transform flowchart config data into ProcessFlowchartForValidation
  if (isPoW) {
    if (powFlowchartConfigQueryResult.type !== QueryResultType.SUCCESS) {
      return {error: ProcedureValidationErrorType.NOT_LOADED};
    }
    const powFlowchartConfigData = powFlowchartConfigQueryResult.data.processFlowchartConfig;
    if (isInaccessible(powFlowchartConfigData)) {
      return {error: ProcedureValidationErrorType.NOT_ACCESSIBLE};
    }
    const powValidationData = buildProcessFlowchartForValidationFromGQL(powFlowchartConfigData);
    if (powValidationData == null) {
      return {error: ProcedureValidationErrorType.NOT_LOADED};
    }
    return {
      processFlowchartForValidation: powValidationData.flowchart,
      designSpace: powValidationData.designSpace,
      procedureType: thinProcedureQueryResult.data.procedure.procedureType,
    };
  }

  // Legacy path: parse the procedure from the flowchart version query
  if (legacyFlowchartVersionQueryResult.type !== QueryResultType.SUCCESS) {
    return {error: ProcedureValidationErrorType.NOT_LOADED};
  }

  const procedure = thinProcedureQueryResult.data.procedure;
  const legacyFlowchartVersion = legacyFlowchartVersionQueryResult.data.procedureFlowchartVersion;
  if (isInaccessible(legacyFlowchartVersion)) {
    return {error: ProcedureValidationErrorType.NOT_ACCESSIBLE};
  }

  const networkProcedure: NetworkProcedure = {
    ...procedure,
    procedureFlowchart: {
      ...procedure.procedureFlowchart,
      // We're stuffing the published version into a field called "latest". This would be dangerous,
      // but it's only used to validate, then tossed.
      latestFlowchartVersion: legacyFlowchartVersion,
    },
  };
  const idToCidMap = generateIdToCidMap(networkProcedure);
  const getCid: (id: string, errorContext: object) => string = (id, errorContext) => {
    const cid = idToCidMap[id];
    if (cid != null) {
      return cid;
    } else {
      throw new Error(`No CID found for ID ${id} in the procedure. Context: ${JSON.stringify(errorContext)}`);
    }
  };
  return {procedureWithMetadata: parseProcedureFromNetwork(networkProcedure, getCid)};
}
